import { Box, Button, Stack, Typography } from '@mui/material';
import type { StepState } from './PropertyStepFlow';
import StepIndicator from './StepIndicator';
import ShowingTimeIntegrated from '../ShowingTimeIntegrated/ShowingTimeIntegrated';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';

interface PropertyJourneyRowsProps {
  steps: StepState[];
  onUpdate?: () => void;
}

const PropertyJourneyRows = ({ steps, onUpdate }: PropertyJourneyRowsProps) => (
  <Stack component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
    {steps.map((step, index) => {
      const tooltip =
        (step.disabled && step.disabledTooltip) || step.tooltipContent;
      const showing = step.id === 'tour' ? step.showingData : undefined;
      const showAction = Boolean(
        step.actionText && (!showing || (step.onClick && !step.disabled))
      );
      let labelColor = 'text.primary';
      if (step.status === 'completed') {
        labelColor = 'success.main';
      } else if (step.disabled) {
        labelColor = 'text.disabled';
      }
      let action = step.actionText;
      if (typeof action === 'string') {
        action =
          step.onClick && !step.disabled ? (
            <Button
              size="small"
              onClick={step.onClick}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {action}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {action}
            </Typography>
          );
      }
      // A plain action ("Schedule", "Draft offer") sits to the right of the
      // label at every width so the row stays one line tall on a phone. Only
      // the tour row's showing details are wide enough to need their own line.
      const stacksOnPhone = Boolean(showing);
      const row = (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: stacksOnPhone
                ? '32px minmax(0, 1fr)'
                : '32px minmax(0, 1fr) auto',
              sm: '32px minmax(0, 1fr) auto',
            },
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 1,
            py: 1,
            borderTop: index > 0 ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <StepIndicator
            status={step.status}
            icon={step.icon}
            size="small"
            disabled={step.disabled}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} color={labelColor}>
              {step.label}
            </Typography>
            {step.sublabel && !step.showingData && (
              <Box sx={{ typography: 'caption', color: 'text.secondary' }}>
                {step.sublabel}
              </Box>
            )}
          </Box>
          {(showing || showAction) && (
            <Stack
              spacing={1}
              sx={{
                gridColumn: { xs: stacksOnPhone ? 2 : 3, sm: 3 },
                gridRow: { xs: stacksOnPhone ? 'auto' : 1, sm: 1 },
                justifySelf: { xs: stacksOnPhone ? 'start' : 'end', sm: 'end' },
                alignItems: {
                  xs: stacksOnPhone ? 'flex-start' : 'flex-end',
                  sm: 'flex-end',
                },
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiButton-root': { textTransform: 'none' },
              }}
            >
              {showing && (
                <ShowingTimeIntegrated showing={showing} onUpdate={onUpdate} />
              )}
              {showAction && action}
            </Stack>
          )}
        </Box>
      );

      return (
        <Box component="li" key={step.id}>
          {tooltip ? (
            <CustomTooltip title={tooltip}>
              <Box>{row}</Box>
            </CustomTooltip>
          ) : (
            row
          )}
        </Box>
      );
    })}
  </Stack>
);

export default PropertyJourneyRows;
